import { styled } from '@mui/material';

import { useMemo } from 'react';

import { isPartialData, PageAction } from '@app-velocity/common';
import {
  TAiSimulationPartialDto,
  TPrototypePartialDto,
} from '@app-velocity/av-api-v2';
import { Tooltip } from '@app-velocity/ui';
import { useI18n } from '@app-velocity/i18n';

import { useReportInternalContex } from '../../../../context/useReportInternalContext';

const StyledDebugContainer = styled('div')({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 1,
});

const StyledTrigger = styled('div')<{
  x: number;
  y: number;
  width: number;
  height: number;
}>(({ x, y, width, height }) => ({
  position: 'absolute',
  left: `${x}%`,
  top: `${y}%`,
  width: `${width}%`,
  height: `${height}%`,
  '> *': {
    height: '100%',
  },
}));

const StyledTriggerContent = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: theme.alpha(theme.palette.primary.main, 0.25),
  border: '2px solid var(--button-button-primary-color)',
  borderRadius: theme.spacing(1),
}));

export type DebugLayerProps = {
  page: {
    actions: PageAction[];
    width: number;
    height: number;
  };
  partials: TPrototypePartialDto[];
  viewPartials: TAiSimulationPartialDto[];
};

export const DebugLayer = ({
  page,
  partials,
  viewPartials,
}: DebugLayerProps) => {
  const { t } = useI18n();
  const { featurePreview } = useReportInternalContex();

  const { partialsActions, pageActions } = useMemo(() => {
    const pageActions = page.actions;
    const partialsActions = partials
      .map((partial) => {
        const viewPartial = viewPartials.find(
          (viewPartial) => viewPartial.id === partial.id
        );

        return {
          partialId: partial.id,
          partialX: viewPartial?.x,
          partialY: viewPartial?.y,
          actions: isPartialData(partial.data) ? partial.data.actions : [],
        };
      })
      .flat();

    return {
      partialsActions,
      pageActions,
    };
  }, [page, partials, viewPartials]);

  const pageWidth = page.width;
  const pageHeight = page.height;

  if (!pageWidth || !pageHeight) return null;

  if (!featurePreview) return null;

  return (
    <StyledDebugContainer>
      {pageActions.map((action) => (
        <StyledTrigger
          x={((action.element?.x || 0) / pageWidth) * 100}
          y={((action.element?.y || 0) / pageHeight) * 100}
          width={((action.element?.width || 0) / pageWidth) * 100}
          height={((action.element?.height || 0) / pageHeight) * 100}
        >
          <Tooltip
            placement="auto"
            key={action.id}
            title={
              <>
                {t('prototypes:action_debug.title', {
                  action: action.action,
                  trigger: action.trigger,
                })}
              </>
            }
          >
            <StyledTriggerContent />
          </Tooltip>
        </StyledTrigger>
      ))}
      {partialsActions.map((partial) => (
        <>
          {partial.actions.map((action) => (
            <StyledTrigger
              x={
                (((partial.partialX || 0) + (action.element?.x || 0)) /
                  pageWidth) *
                100
              }
              y={
                (((partial.partialY || 0) + (action.element?.y || 0)) /
                  pageHeight) *
                100
              }
              width={((action.element?.width || 0) / pageWidth) * 100}
              height={((action.element?.height || 0) / pageHeight) * 100}
            >
              <Tooltip
                placement="bottom"
                key={action.id}
                title={
                  <>
                    {t('prototypes:action_debug.title', {
                      action: action.action,
                      trigger: action.trigger,
                    })}
                  </>
                }
              >
                <StyledTriggerContent />
              </Tooltip>
            </StyledTrigger>
          ))}
        </>
      ))}
    </StyledDebugContainer>
  );
};
